import {
  Building2,
  Clock,
  DollarSign,
  FileText,
  Phone,
  Settings as SettingsIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import type { GetPublicSettings } from '@virtual-care/client';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  Textarea,
} from '@virtual-care/shadcn';
import { SearchInput, useAction, useData } from '@virtual-care/ui';

import { PageHeader } from '../../components/page-header.tsx';

export default function SettingsV2Page() {
  const [activeSection, setActiveSection] = useState('general');
  const { data: publicSettings } = useData('GET /settings/public');

  // Track settings data version to reset form when data changes
  const [settingsVersion, setSettingsVersion] = useState<string | null>(null);
  const currentVersion = publicSettings ? JSON.stringify(publicSettings) : null;

  const [generalSettings, setGeneralSettings] = useState({
    companyName: '',
    companyAddress: '',
    companyEmail: '',
    supportPhoneNumber: '',
    homecareEnabled: false,
  });
  const [legalSettings, setLegalSettings] = useState({
    termsAndConditionsUrl: '',
  });
  const [billingSettings, setBillingSettings] = useState({
    appointmentFee: '',
    secondOpinionFee: '',
    secondOpinionUrgentFee: '',
  });
  const [schedulingSettings, setSchedulingSettings] = useState({
    followupWindowDays: '',
    maxFollowupCount: '',
  });

  // Sync form state when settings data changes
  if (currentVersion && currentVersion !== settingsVersion) {
    setSettingsVersion(currentVersion);
    setGeneralSettings({
      companyName: publicSettings?.companyName?.value || '',
      companyAddress: publicSettings?.companyAddress?.value || '',
      companyEmail: publicSettings?.companyEmail?.value || '',
      supportPhoneNumber: publicSettings?.supportPhoneNumber?.value || '',
      homecareEnabled: publicSettings?.homecareEnabled?.value === 'true',
    });
    setLegalSettings({
      termsAndConditionsUrl: publicSettings?.termsAndConditionsUrl?.value || '',
    });
    setBillingSettings({
      appointmentFee: publicSettings?.appointmentFee?.value || '',
      secondOpinionFee: publicSettings?.secondOpinionFee?.value || '',
      secondOpinionUrgentFee:
        publicSettings?.secondOpinionUrgentFee?.value || '',
    });
    setSchedulingSettings({
      followupWindowDays: publicSettings?.followupWindowDays?.value || '',
      maxFollowupCount: publicSettings?.maxFollowupCount?.value || '',
    });
  }

  const { mutate: bulkUpdateSettings, isPending } = useAction(
    'PATCH /settings/bulk',
    { invalidate: ['GET /settings/public'] },
  );

  const handleSaveGeneral = () => {
    const settings = [
      {
        key: 'companyName',
        value: generalSettings.companyName || '',
        description: 'Company name displayed to users',
        section: 'general',
      },
      {
        key: 'companyAddress',
        value: generalSettings.companyAddress || '',
        description: 'Company physical address',
        section: 'general',
      },
      {
        key: 'companyEmail',
        value: generalSettings.companyEmail || '',
        description: 'Main company email address',
        section: 'general',
      },
      {
        key: 'supportPhoneNumber',
        value: generalSettings.supportPhoneNumber || '',
        description: 'Customer support phone number',
        section: 'general',
      },
    ].filter((setting) => setting.value.trim() !== '');

    // Always include homecareEnabled toggle
    settings.push({
      key: 'homecareEnabled',
      value: generalSettings.homecareEnabled ? 'true' : 'false',
      description: 'Show home care services to patients',
      section: 'general',
    });

    bulkUpdateSettings({ settings });
  };

  const handleSaveLegal = () => {
    const settings = [
      {
        key: 'termsAndConditionsUrl',
        value: legalSettings.termsAndConditionsUrl || '',
        description: 'Terms, conditions and privacy policy URL',
        section: 'legal',
      },
    ].filter((setting) => setting.value.trim() !== '');

    if (settings.length > 0) {
      bulkUpdateSettings({ settings });
    } else {
      toast.error('Please fill in at least one field');
    }
  };

  const handleSaveBilling = () => {
    const settings = [
      {
        key: 'appointmentFee',
        value: billingSettings.appointmentFee || '',
        description: 'Standard fee for a virtual consultation',
        section: 'billing',
      },
      {
        key: 'secondOpinionFee',
        value: billingSettings.secondOpinionFee || '',
        description: 'Second opinion consultation fee',
        section: 'billing',
      },
      {
        key: 'secondOpinionUrgentFee',
        value: billingSettings.secondOpinionUrgentFee || '',
        description: 'Second opinion urgent consultation fee',
        section: 'billing',
      },
    ].filter((setting) => setting.value.trim() !== '');

    if (settings.length > 0) {
      bulkUpdateSettings({ settings });
    } else {
      toast.error('Please fill in at least one field');
    }
  };

  const handleSaveScheduling = () => {
    const settings = [
      {
        key: 'followupWindowDays',
        value: schedulingSettings.followupWindowDays || '',
        description: 'Number of days for followup appointment window',
        section: 'scheduling',
      },
      {
        key: 'maxFollowupCount',
        value: schedulingSettings.maxFollowupCount || '',
        description:
          'Maximum number of followup appointments allowed per reservation',
        section: 'scheduling',
      },
    ].filter((setting) => setting.value.trim() !== '');

    if (settings.length > 0) {
      bulkUpdateSettings({ settings });
    } else {
      toast.error('Please fill in at least one field');
    }
  };

  const navigationItems = [
    { id: 'general', label: 'General', icon: Building2 },
    { id: 'legal', label: 'Legal & Terms', icon: FileText },
    { id: 'billing', label: 'Billing', icon: DollarSign },
    { id: 'scheduling', label: 'Scheduling', icon: Clock },
  ];

  return (
    <div className="w-full">
      <PageHeader
        title="Settings"
        description="Manage your application settings and configuration"
        icon={SettingsIcon}
      />

      <div className="grid w-full items-start gap-6 md:grid-cols-[180px_1fr] lg:grid-cols-[250px_1fr]">
        <nav className="grid gap-4 text-sm">
          <SearchInput
            className="min-w-auto"
            placeholder="Search settings..."
            onChange={(query) => {
              const matchingSection = findMatchingSection(
                publicSettings,
                query,
              );
              if (matchingSection) {
                setActiveSection(matchingSection);
              }
            }}
          />
          {navigationItems.map((item) => {
            const Icon = item.icon;
            return (
              <Button
                variant={activeSection === item.id ? 'default' : 'outline'}
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`flex cursor-pointer justify-start gap-2 rounded-md px-3 py-2 text-left transition-colors`}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </Button>
            );
          })}
        </nav>

        <div className="grid gap-6">
          {activeSection === 'general' && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Company Information
                  </CardTitle>
                  <CardDescription>
                    Basic information about your company that will be displayed
                    to users.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-2">
                    <Label htmlFor="companyName">Company Name</Label>
                    <Input
                      id="companyName"
                      value={generalSettings.companyName || ''}
                      onChange={(e) =>
                        setGeneralSettings((prev) => ({
                          ...prev,
                          companyName: e.target.value,
                        }))
                      }
                      placeholder="Phi Digital"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="companyAddress">Company Address</Label>
                    <Textarea
                      id="companyAddress"
                      value={generalSettings.companyAddress || ''}
                      onChange={(e) =>
                        setGeneralSettings((prev) => ({
                          ...prev,
                          companyAddress: e.target.value,
                        }))
                      }
                      placeholder="123 Medical Street, Healthcare District, City"
                      rows={3}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="companyEmail">Company Email</Label>
                    <Input
                      id="companyEmail"
                      type="email"
                      value={generalSettings.companyEmail || ''}
                      onChange={(e) =>
                        setGeneralSettings((prev) => ({
                          ...prev,
                          companyEmail: e.target.value,
                        }))
                      }
                      placeholder="contact@virtualcare.com"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-4">
                    <Checkbox
                      id="homecareEnabled"
                      checked={generalSettings.homecareEnabled}
                      onCheckedChange={(checked: boolean) =>
                        setGeneralSettings((prev) => ({
                          ...prev,
                          homecareEnabled: checked,
                        }))
                      }
                    />
                    <div className="grid gap-1">
                      <Label htmlFor="homecareEnabled">
                        Enable Home Care Services
                      </Label>
                      <p className="text-muted-foreground text-xs">
                        Show home care option to patients on the home page
                      </p>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="border-t">
                  <Button onClick={handleSaveGeneral} disabled={isPending}>
                    {isPending ? 'Saving...' : 'Save'}
                  </Button>
                </CardFooter>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5" />
                    Support Contact
                  </CardTitle>
                  <CardDescription>
                    Contact information for customer support.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-2">
                    <Label htmlFor="supportPhone">Support Phone Number</Label>
                    <Input
                      id="supportPhone"
                      value={generalSettings.supportPhoneNumber || ''}
                      onChange={(e) =>
                        setGeneralSettings((prev) => ({
                          ...prev,
                          supportPhoneNumber: e.target.value,
                        }))
                      }
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </CardContent>
                <CardFooter className="border-t">
                  <Button onClick={handleSaveGeneral} disabled={isPending}>
                    {isPending ? 'Saving...' : 'Save'}
                  </Button>
                </CardFooter>
              </Card>
            </>
          )}

          {/* Legal & Terms Settings */}
          {activeSection === 'legal' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Legal Documents
                </CardTitle>
                <CardDescription>
                  URL to your terms of service and privacy policy page.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="termsUrl">Terms & Privacy URL</Label>
                  <Input
                    id="termsUrl"
                    type="url"
                    value={legalSettings.termsAndConditionsUrl || ''}
                    onChange={(e) =>
                      setLegalSettings((prev) => ({
                        ...prev,
                        termsAndConditionsUrl: e.target.value,
                      }))
                    }
                    placeholder="https://yoursite.com/terms-and-privacy"
                  />
                  <p className="text-muted-foreground text-xs">
                    Single page containing both terms of service and privacy
                    policy
                  </p>
                </div>
              </CardContent>
              <CardFooter className="border-t">
                <Button onClick={handleSaveLegal} disabled={isPending}>
                  {isPending ? 'Saving...' : 'Save'}
                </Button>
              </CardFooter>
            </Card>
          )}

          {/* Billing Settings */}
          {activeSection === 'billing' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Billing & Fees
                </CardTitle>
                <CardDescription>
                  Configure consultation fees and billing amounts (in SAR).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="appointmentFee">
                    Standard Appointment Fee (SAR)
                  </Label>
                  <Input
                    id="appointmentFee"
                    type="number"
                    min="0"
                    step="0.01"
                    value={billingSettings.appointmentFee || ''}
                    onChange={(e) =>
                      setBillingSettings((prev) => ({
                        ...prev,
                        appointmentFee: e.target.value,
                      }))
                    }
                    placeholder="200"
                  />
                  <p className="text-muted-foreground text-xs">
                    Fee charged for a standard virtual consultation
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="secondOpinionFee">
                    Second Opinion Fee (SAR)
                  </Label>
                  <Input
                    id="secondOpinionFee"
                    type="number"
                    min="0"
                    step="0.01"
                    value={billingSettings.secondOpinionFee || ''}
                    onChange={(e) =>
                      setBillingSettings((prev) => ({
                        ...prev,
                        secondOpinionFee: e.target.value,
                      }))
                    }
                    placeholder="100"
                  />
                  <p className="text-muted-foreground text-xs">
                    Fee charged for a second opinion consultation
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="secondOpinionUrgentFee">
                    Second Opinion Urgent Fee (SAR)
                  </Label>
                  <Input
                    id="secondOpinionUrgentFee"
                    type="number"
                    min="0"
                    step="0.01"
                    value={billingSettings.secondOpinionUrgentFee || ''}
                    onChange={(e) =>
                      setBillingSettings((prev) => ({
                        ...prev,
                        secondOpinionUrgentFee: e.target.value,
                      }))
                    }
                    placeholder="150"
                  />
                  <p className="text-muted-foreground text-xs">
                    Fee charged for an urgent second opinion consultation
                  </p>
                </div>
              </CardContent>
              <CardFooter className="border-t">
                <Button onClick={handleSaveBilling} disabled={isPending}>
                  {isPending ? 'Saving...' : 'Save'}
                </Button>
              </CardFooter>
            </Card>
          )}

          {activeSection === 'scheduling' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Scheduling Configuration
                </CardTitle>
                <CardDescription>
                  Configure appointment scheduling rules and time windows
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="followupWindowDays">
                    Followup Window (Days)
                  </Label>
                  <Input
                    id="followupWindowDays"
                    type="number"
                    max="30"
                    step="1"
                    value={schedulingSettings.followupWindowDays || ''}
                    onChange={(e) =>
                      setSchedulingSettings((prev) => ({
                        ...prev,
                        followupWindowDays: e.target.value,
                      }))
                    }
                    placeholder="14"
                  />
                  <p className="text-muted-foreground text-xs">
                    Number of days patients have to schedule a followup
                    appointment
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="maxFollowupCount">Max Followup Count</Label>
                  <Input
                    id="maxFollowupCount"
                    type="number"
                    min="1"
                    max="10"
                    step="1"
                    value={schedulingSettings.maxFollowupCount || ''}
                    onChange={(e) =>
                      setSchedulingSettings((prev) => ({
                        ...prev,
                        maxFollowupCount: e.target.value,
                      }))
                    }
                    placeholder="2"
                  />
                  <p className="text-muted-foreground text-xs">
                    Maximum number of followup appointments per reservation
                  </p>
                </div>
              </CardContent>
              <CardFooter className="border-t">
                <Button onClick={handleSaveScheduling} disabled={isPending}>
                  {isPending ? 'Saving...' : 'Save'}
                </Button>
              </CardFooter>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

const findMatchingSection = (
  settings: GetPublicSettings | undefined,
  query?: string,
) => {
  if (!query?.trim()) return null;
  if (!settings) return null;

  const lowerQuery = query.toLowerCase();

  for (const [key, { section, value, description }] of Object.entries(
    settings,
  )) {
    const lowerKey = key.toLowerCase();
    const lowerValue = value.toLowerCase();
    const lowerDescription = description.toLowerCase();
    const lowerSection = section.toLowerCase();
    if (
      lowerSection.includes(lowerQuery) ||
      lowerKey.includes(lowerQuery) ||
      lowerValue.includes(lowerQuery) ||
      lowerDescription.includes(lowerQuery)
    ) {
      return section;
    }
  }
  return null;
};
